import React, { useState, useEffect } from 'react';
import { EyeIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, transactionApi } from '../utils/api';
import type { TransactionDto } from '../types/wallet';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface TeacherOption {
  id: string;
  fullName: string;
}

interface Filters {
  studentId: string;
  teacherId: string;
  type: string;
  fromDate: string;
  toDate: string;
}

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  PaymentGatewayCharge: 'شحن عبر بوابة الدفع',
  WalletCodeCharge: 'شحن بكود المحفظة',
  CoinsUsage: 'استخدام العملات',
  DirectCoursePurchase: 'شراء مباشر للباقة',
};

const Transactions: React.FC = () => {
  const { isSaeedAdmin, isAssistant, hasPrivilege } = useUser();
  const canViewTransactions = isSaeedAdmin() || (isAssistant() && hasPrivilege(P.Transaction_View));
  const [transactions, setTransactions] = useState<TransactionDto[]>([]);
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage] = useState(10);

  const [filters, setFilters] = useState<Filters>({
    studentId: '',
    teacherId: '',
    type: '',
    fromDate: '',
    toDate: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    studentId: '',
    teacherId: '',
    type: '',
    fromDate: '',
    toDate: '',
  });

  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDto | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await authFetch(apiUrl('Teacher'), {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authService.getToken()}`,
          },
        });
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setTeachers(
            data.data.map((t: { id: string; fullName: string }) => ({
              id: t.id,
              fullName: t.fullName,
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };

    fetchTeachers();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const data = await transactionApi.getAll({
          pageNumber: currentPage,
          pageSize: itemsPerPage,
          studentId: appliedFilters.studentId || undefined,
          teacherId: appliedFilters.teacherId || undefined,
          type: appliedFilters.type || undefined,
          fromDate: appliedFilters.fromDate ? `${appliedFilters.fromDate}T00:00:00Z` : undefined,
          toDate: appliedFilters.toDate ? `${appliedFilters.toDate}T23:59:59Z` : undefined,
        });

        if (data.success) {
          setTransactions(data.data?.data || []);
          setTotalItems(data.data?.totalCount || 0);
          setTotalPages(data.data?.totalPages || 0);
        }
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [appliedFilters, currentPage, itemsPerPage]);

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const resetFilters: Filters = {
      studentId: '',
      teacherId: '',
      type: '',
      fromDate: '',
      toDate: '',
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'CoinsUsage':
        return 'bg-red-100 text-red-800';
      case 'WalletCodeCharge':
      case 'PaymentGatewayCharge':
        return 'bg-green-100 text-green-800';
      case 'DirectCoursePurchase':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!canViewTransactions) {
    return (
      <div className="p-6" dir="rtl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">ليس لديك صلاحية لعرض التحويلات.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      <h1 className="text-2xl font-bold mb-4">التحويلات</h1>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">معرف الطالب</label>
            <input
              type="text"
              value={filters.studentId}
              onChange={(e) => setFilters({ ...filters, studentId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="معرف الطالب"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">المعلم</label>
            <select
              value={filters.teacherId}
              onChange={(e) => setFilters({ ...filters, teacherId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">كل المعلمين</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {teacher.fullName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">نوع التحويل</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">كل الأنواع</option>
              <option value="PaymentGatewayCharge">شحن عبر بوابة الدفع</option>
              <option value="WalletCodeCharge">شحن بكود المحفظة</option>
              <option value="CoinsUsage">استخدام العملات</option>
              <option value="DirectCoursePurchase">شراء مباشر للباقة</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">من تاريخ</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">إلى تاريخ</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-start space-x-reverse space-x-4">
          <button
            onClick={handleSearch}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            بحث
          </button>
          <button
            onClick={handleResetFilters}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            إعادة تعيين
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">المجموع: {totalItems} تحويل</span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">اسم الطالب</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">نوع التحويل</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">المبلغ</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الرصيد بعد</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">المعلم</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">التاريخ</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <svg
                        className="animate-spin h-8 w-8 text-primary"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    لا توجد تحويلات
                  </td>
                </tr>
              ) : (
                transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{transaction.studentName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTransactionTypeColor(transaction.type)}`}>
                        {TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      <span className={transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {transaction.amount >= 0 ? '+' : ''}
                        {transaction.amount} عملة
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{transaction.balanceAfter} عملة</td>
                    <td className="px-6 py-4 whitespace-nowrap">{transaction.teacherName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(transaction.createdAt).toLocaleString('ar-EG')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setSelectedTransaction(transaction);
                          setShowDetailsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && !loading && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              عرض {(currentPage - 1) * itemsPerPage + 1} إلى{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} من {totalItems}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-1 border rounded ${
                  currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                السابق
              </button>
              <span className="px-3 py-1">
                صفحة {currentPage} من {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-1 border rounded ${
                  currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                التالي
              </button>
            </div>
          </div>
        )}
      </div>

      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">تفاصيل التحويل</h2>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">اسم الطالب:</span>
                  <p className="text-base">{selectedTransaction.studentName}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">معرف الطالب:</span>
                  <p className="text-base text-sm break-all">{selectedTransaction.studentId}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">نوع التحويل:</span>
                  <p className="text-base">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTransactionTypeColor(selectedTransaction.type)}`}>
                      {TRANSACTION_TYPE_LABELS[selectedTransaction.type] || selectedTransaction.type}
                    </span>
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">المبلغ:</span>
                  <p
                    className={`text-base font-medium ${
                      selectedTransaction.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {selectedTransaction.amount >= 0 ? '+' : ''}
                    {selectedTransaction.amount} عملة
                  </p>
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">الرصيد بعد التحويل:</span>
                <p className="text-base">{selectedTransaction.balanceAfter} عملة</p>
              </div>

              {selectedTransaction.walletCodeCode && (
                <div>
                  <span className="text-sm font-medium text-gray-500">كود المحفظة:</span>
                  <p className="text-base">{selectedTransaction.walletCodeCode}</p>
                </div>
              )}

              {selectedTransaction.paymentGatewayOrderId && (
                <div>
                  <span className="text-sm font-medium text-gray-500">رقم طلب الدفع:</span>
                  <p className="text-base">{selectedTransaction.paymentGatewayOrderId}</p>
                </div>
              )}

              {selectedTransaction.itemType && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">نوع العنصر:</span>
                    <p className="text-base">
                      {selectedTransaction.itemType === 'Course' ? 'باقة' : 'وحدة'}
                    </p>
                  </div>
                  {selectedTransaction.itemPrice != null && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">سعر العنصر:</span>
                      <p className="text-base">{selectedTransaction.itemPrice} عملة</p>
                    </div>
                  )}
                </div>
              )}

              {selectedTransaction.teacherName && (
                <div>
                  <span className="text-sm font-medium text-gray-500">المعلم:</span>
                  <p className="text-base">{selectedTransaction.teacherName}</p>
                </div>
              )}

              <div>
                <span className="text-sm font-medium text-gray-500">الوصف:</span>
                <p className="text-base">{selectedTransaction.description || 'لا يوجد'}</p>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-500">التاريخ:</span>
                <p className="text-base">
                  {new Date(selectedTransaction.createdAt).toLocaleString('ar-EG')}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-start">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedTransaction(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
